import { useEffect, useRef } from "react";

const VICTORY_MP3 = "/victory.mp3";
const GAMEOVER_MP3 = "/gameover.mp3";

export interface UseVictoryMusicOptions {
  /** When true, result track plays and BGM should be paused. */
  isGameFinished: boolean;
  isSoundEnabled: boolean;
  gameVolume: number;
  /** Optional ref to the BGM audio element to pause while result plays. */
  bgmAudioRef?: React.RefObject<HTMLAudioElement | null>;
  /** 'timeout' = loss, play /gameover.mp3; otherwise play /victory.mp3. Omit for victory-only modes. */
  endResult?: "win" | "timeout";
}

/**
 * When game finishes: pauses BGM (if ref provided), plays /victory.mp3 (win) or /gameover.mp3 (loss).
 * When game is no longer finished or component unmounts: stops result sound and resumes BGM.
 */
export function useVictoryMusic({
  isGameFinished,
  isSoundEnabled,
  gameVolume,
  bgmAudioRef,
  endResult,
}: UseVictoryMusicOptions): void {
  const resultAudioRef = useRef<HTMLAudioElement | null>(null);
  const wePausedBgmRef = useRef(false);

  useEffect(() => {
    if (!isGameFinished) return;

    const bgmEl = bgmAudioRef?.current;
    if (bgmEl) {
      wePausedBgmRef.current = !bgmEl.paused;
      bgmEl.pause();
    }

    if (!isSoundEnabled || gameVolume <= 0) return;

    const src = endResult === "timeout" ? GAMEOVER_MP3 : VICTORY_MP3;
    const prev = resultAudioRef.current;
    if (prev) {
      prev.pause();
      prev.currentTime = 0;
    }
    const el = new Audio(src);
    resultAudioRef.current = el;
    el.volume = Math.max(0, Math.min(1, gameVolume));
    el.currentTime = 0;
    el.play().catch(() => {});

    return () => {
      el?.pause();
      el.currentTime = 0;
      if (bgmEl && wePausedBgmRef.current) {
        wePausedBgmRef.current = false;
        bgmEl.play().catch(() => {});
      }
    };
  }, [isGameFinished, isSoundEnabled, gameVolume, bgmAudioRef, endResult]);

  useEffect(() => {
    return () => {
      const el = resultAudioRef.current;
      if (el) {
        el.pause();
        el.currentTime = 0;
      }
      if (wePausedBgmRef.current && bgmAudioRef?.current) {
        wePausedBgmRef.current = false;
        bgmAudioRef.current.play().catch(() => {});
      }
    };
  }, [bgmAudioRef]);
}
